import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import type { TopIdleEstado } from '../../api/types';
import { aoCorreio } from '../../correio/aviso';

/**
 * O estado do botão Votar do chat (docs/techspec/techspec-topidle-votos.md §04). Quem decide se a conta pode votar
 * é o servidor; aqui só se pergunta na hora certa:
 * - ao montar (com um cache de 60 s entre o chat aberto e o da folha do celular);
 * - quando o próximo voto chega (um timer marcado para `proximoVotoEm`, sem ficar perguntando);
 * - quando a aba volta a ficar visível;
 * - depois do clique, a cada 30 s por até 10 min, ou na hora em que a carta do voto chega (aviso 204).
 */
export const ESPERA_DO_VOTO_MS = 10 * 60_000;
const PERGUNTA_NA_ESPERA_MS = 30_000;
const CACHE_MS = 60_000;
/** O setTimeout do navegador estoura acima de ~24,8 dias. */
const TIMER_MAX_MS = 2 ** 31 - 1;

let cache: { estado: TopIdleEstado; em: number } | null = null;

export interface VotoTopIdle {
  readonly estado: TopIdleEstado | null;
  /** Clicou em Votar e o voto ainda não chegou. */
  readonly aguardando: boolean;
  /** Para a contagem do botão apagado (atualiza a cada 30 s). */
  readonly agora: number;
  readonly votou: () => void;
}

export function useVotoTopIdle(): VotoTopIdle {
  const [estado, setEstado] = useState<TopIdleEstado | null>(cache?.estado ?? null);
  const [aguardandoAte, setAguardandoAte] = useState<number | null>(null);
  const [agora, setAgora] = useState(() => Date.now());
  const vivo = useRef(true);

  const ler = useCallback(async () => {
    try {
      const e = await api.topidleEstado();
      cache = { estado: e, em: Date.now() };
      if (!vivo.current) return;
      setEstado(e);
      setAgora(Date.now());
      // O voto chegou: acabou a espera.
      if (!e.podeVotar || !e.ligado) setAguardandoAte(null);
    } catch {
      // Sem resposta, o botão fica como estava (ou não aparece): o voto nunca é condição para jogar.
    }
  }, []);

  useEffect(() => {
    vivo.current = true;
    if (!cache || Date.now() - cache.em > CACHE_MS) void ler();
    const visivel = () => { if (document.visibilityState === 'visible') void ler(); };
    document.addEventListener('visibilitychange', visivel);
    const fora = aoCorreio(() => void ler());
    return () => {
      vivo.current = false;
      document.removeEventListener('visibilitychange', visivel);
      fora();
    };
  }, [ler]);

  // Apagado: a contagem anda de 30 em 30 s e o botão acende sozinho na hora do próximo voto.
  const proximo = estado?.ligado && !estado.podeVotar ? estado.proximoVotoEm : null;
  useEffect(() => {
    if (proximo == null) return;
    const relogio = window.setInterval(() => setAgora(Date.now()), 30_000);
    const acender = window.setTimeout(() => void ler(), Math.min(TIMER_MAX_MS, Math.max(1000, proximo - Date.now() + 1000)));
    return () => { window.clearInterval(relogio); window.clearTimeout(acender); };
  }, [proximo, ler]);

  // Depois do clique: pergunta de 30 em 30 s até o voto chegar ou passar de 10 min.
  useEffect(() => {
    if (aguardandoAte == null) return;
    const pergunta = window.setInterval(() => void ler(), PERGUNTA_NA_ESPERA_MS);
    const desiste = window.setTimeout(() => setAguardandoAte(null), Math.max(0, aguardandoAte - Date.now()));
    return () => { window.clearInterval(pergunta); window.clearTimeout(desiste); };
  }, [aguardandoAte, ler]);

  const votou = useCallback(() => setAguardandoAte(Date.now() + ESPERA_DO_VOTO_MS), []);

  return { estado, aguardando: aguardandoAte != null, agora, votou };
}

/** "14 h 20 min", "35 min" ou "1 min": o que falta para o próximo voto (arredonda para cima). */
export function tempoAteVotar(ms: number): { h: number; min: number } {
  const total = Math.max(1, Math.ceil(ms / 60_000));
  return { h: Math.floor(total / 60), min: total % 60 };
}
